import { apiGet } from '../Api_Functions'
import { baseLanguageCodeOf, contentCodeFor, needsFetchedContent } from './customLanguage'

/**
 * Historia 138 — the language catalogue and how a language is actually applied.
 *
 * Two routes, both authenticated and both outside `/endpoints/backoffice/**` on purpose, because any
 * signed-in user needs them to see the application in their own words:
 *   - `GET languages` — every language, standard and custom.
 *   - `GET languages/{code}/file` — the customer's flat dictionary, handed to i18next as-is.
 *
 * The rules that are easy to get wrong and are therefore concentrated here:
 *   - **The content is requested on exactly two occasions** — when the authenticated session is
 *     established, and on every change from the selector, *including re-picking the language already
 *     in use. Never on a timer, on window focus, or between screens: it is ~180 KB with no cache,
 *     and the lack of cache is the point (the file is edited by hand and the edit has to show).
 *   - **Register, then apply.** `react-i18next` does not re-render when resources are added to a
 *     language that is already active, so registering after applying is the easy way to ship a
 *     first paint in the wrong words.
 *   - **Nothing ever leaves the user without text.** A catalogue that does not arrive degrades to
 *     the eight that ship with the application; a dictionary that does not arrive falls back to the
 *     base language, with no error on screen and the language still in the selector.
 *   - **An empty dictionary is not a missing one** (amendment 4). A custom language is registered
 *     with four fields and no content, so its file is born empty and the language runs on its base
 *     through i18next's own hierarchy until a key is redefined. Only an unreadable file degrades.
 */

/**
 * The eight that ship inside the application, in the order the selector has always shown them.
 *
 * This is the floor, not a cache: it is what the selector falls back to when the catalogue cannot be
 * read, so that a failed request can never leave the user unable to change language [R4/S27].
 */
export const STANDARD_LANGUAGES = ['es', 'en', 'eus', 'pt', 'fr', 'ca', 'de', 'lv'].map((code) => ({
  code,
  name: code.toUpperCase(),
  custom: false,
  baseLanguageCode: null,
  replacesBase: false
}))

/**
 * Reads the catalogue, degrading to the eight standard languages rather than failing.
 *
 * ⚠️ `Api_Functions` turns an unreadable body into `[]`, so an empty array is indistinguishable from
 * a broken answer. Either way the honest move is the same: fall back to the eight. An installation
 * really has at least the eight, so an empty catalogue is never a legitimate state.
 *
 * @returns {Promise<Array<Object>>} The catalogue, never empty
 */
/**
 * How long each language read is given before the application gives up on it and carries on.
 *
 * ⚠️ **This is not tuning, it is the hole the design admitted having.** Both reads swallow their own
 * failures, so the comment in `useUserLanguage` said the only way to hang was "a request that never
 * settles" -- and on 2026-09-18 that is exactly what happened while testing: ten minutes on a blank
 * screen, because the authenticated tree is held until the language is settled and a promise that
 * never resolves is never settled. A request that never answers is now treated as the same thing as
 * one that answers badly: the catalogue falls back to the eight, the dictionary to the base
 * language. Nobody is left staring at nothing.
 *
 * The content is the slow one on purpose -- it is ~175 KB with no cache -- so it gets the longer
 * budget.
 */
const CATALOGUE_TIMEOUT_MS = 10000
const CONTENT_TIMEOUT_MS = 20000

/**
 * Resolves with whatever the promise gives, or with `fallback` when it takes too long.
 *
 * ⚠️ The slow promise is NOT cancelled -- `fetch` cannot be un-sent from here -- it is abandoned. If
 * it ever answers, its value is dropped, which is the honest outcome: by then the interface is
 * already running on the language it settled for.
 *
 * @param {Promise} promise The read to bound
 * @param {number} milliseconds How long to wait
 * @param {*} fallback What to answer with when the wait runs out
 * @returns {Promise} The value, or the fallback
 */
const withTimeout = (promise, milliseconds, fallback) => {
  let timer
  const expiry = new Promise((resolve) => {
    timer = setTimeout(() => resolve(fallback), milliseconds)
  })
  return Promise.race([promise, expiry]).finally(() => clearTimeout(timer))
}

export const fetchLanguageCatalogue = async () => {
  try {
    const catalogue = await withTimeout(apiGet('languages'), CATALOGUE_TIMEOUT_MS, null)
    return Array.isArray(catalogue) && catalogue.length > 0 ? catalogue : STANDARD_LANGUAGES
  } catch (error) {
    return STANDARD_LANGUAGES
  }
}

/**
 * The options the selector offers.
 *
 * A language that REPLACES its base is not an option of its own [S26]: replacing is not hiding. The
 * base stays in the list and stays choosable; what changes is only which dictionary is loaded when
 * it is chosen.
 *
 * @param {Array<Object>} catalogue The catalogue
 * @returns {Array<Object>} The entries to show, in catalogue order
 */
export const selectableLanguages = (catalogue) =>
  (Array.isArray(catalogue) ? catalogue : STANDARD_LANGUAGES).filter((language) => language?.replacesBase !== true)

/**
 * The text an option shows.
 *
 * ⚠️ Reading of S24 worth a reviewer's eye: the eight keep the short uppercase code they have always
 * shown, so the selector looks exactly as it does today, and a custom language gets the same
 * treatment. The `name` from the catalogue is the fallback, which is what guarantees the other
 * clause of that scenario -- no option is ever left with no visible text.
 *
 * @param {Object} language A catalogue entry
 * @returns {string} The label
 */
export const languageLabel = (language) =>
  language?.code ? language.code.toUpperCase() : (language?.name || '')

/** What `fetchLanguageContent` answers when the dictionary cannot be read at all. */
const CONTENT_NOT_AVAILABLE = Object.freeze({ available: false, content: {} })

/**
 * Reads the dictionary of a custom language, saying apart "it is not there" from "it is there and
 * still redefines nothing".
 *
 * ⚠️ **Amendment 4 made the second answer a normal one, not an accident.** Since the registration
 * sends four fields and no dictionary, a custom language is BORN with an empty file: every one of
 * its texts comes from its base until somebody redefines one. So `200 {}` is the ordinary state of
 * day one, and it must apply the custom language -- i18next resolves `es-acme → es → en` on its
 * own. Only a file that cannot be read (404, network, timeout, a body that is not a dictionary)
 * degrades to the base language.
 *
 * @param {string} contentCode Code of the language to read
 * @returns {Promise<{available: boolean, content: Object}>} Whether the file could be read, and the
 *   flat dictionary -- possibly empty -- when it could
 */
export const fetchLanguageContent = async (contentCode) => {
  try {
    // Bounded like the catalogue: a dictionary that never arrives is the same as one that cannot be
    // read, and the answer to that is already written -- run on the base language.
    const content = await withTimeout(apiGet(`languages/${contentCode}/file`), CONTENT_TIMEOUT_MS, undefined)
    // The contract forbids a wrapper: the body IS the resource bundle. Anything that is not a plain
    // object -- including the `[]` that `Api_Functions` produces from an unreadable body -- is
    // treated as "not available", which is the path that keeps the base language on screen.
    const isDictionary = content !== null && typeof content === 'object' && !Array.isArray(content)
    return isDictionary ? { available: true, content } : CONTENT_NOT_AVAILABLE
  } catch (error) {
    return CONTENT_NOT_AVAILABLE
  }
}

/**
 * The keys of a flat dictionary whose value is not blank -- not `''`, and not only whitespace,
 * including the NBSP (U+00A0) a hand edit is prone to leaving behind [RN34b, DG2-0 H1].
 *
 * ⚠️ **Why this has to run before registering, not be left to i18next.** i18next 22 defaults to
 * `returnEmptyString: true`, so an `""` that reaches `addResourceBundle` paints an empty label with
 * no warning anywhere. Dropping the key here instead lets it resolve by inheritance -- base → `en` →
 * the key itself -- exactly like a key that was never redefined at all.
 *
 * `String.prototype.trim()` already strips the NBSP: it is Unicode `WhiteSpace`, which the ECMAScript
 * spec includes, so a plain `.trim() === ''` catches spaces, tabs, newlines and NBSP alike.
 *
 * @param {Object} content The flat dictionary read from `GET languages/{code}/file`
 * @returns {Object} The same dictionary, without the blank-valued keys
 */
export const discardBlankValues = (content) => {
  const source = content && typeof content === 'object' ? content : {}
  return Object.keys(source).reduce((kept, key) => {
    const value = source[key]
    if (typeof value === 'string' && value.trim() !== '') {
      kept[key] = value
    }
    return kept
  }, {})
}

/**
 * Registers the dictionary of `code` under the `translation` namespace, SUBSTITUTING whatever was
 * already registered there instead of merging with it (RN33, CD-13).
 *
 * ⚠️ **The historia 138 defect this replaces.** `i18n.addResourceBundle(code, 'translation',
 * content, true, true)` on its own, with `deep=true`, MERGES with an existing bundle under the same
 * code: a key deleted from a fresh read keeps resolving to its stale, merged-in value until the page
 * reloads. Removing the existing bundle first is what makes "substitute" true.
 *
 * ⚠️ **Risk R10.** `i18n.removeResourceBundle` (i18next 22, `i18next.js:447-454`) also drops the
 * namespace itself if nothing re-adds it. `addResourceBundle` right after is what reconstitutes it —
 * skipping that step would leave `code` a known language with no translation namespace at all, and
 * every key would resolve to the raw key instead of falling back to its base.
 *
 * The single call site for `discardBlankValues` (RN34b): every custom dictionary this application
 * ever registers -- start-up, a pick from the selector, and CU11's session-side re-read -- goes
 * through here, so blank values are dropped in all three at once. It never touches the eight standard
 * dictionaries (`src/i18n.js`), which are registered by `i18n.init` itself, never through here.
 *
 * @param {Object} i18n The i18next instance
 * @param {string} code Language code to (re)register
 * @param {Object} content The flat dictionary to register
 */
export const applyCustomDictionary = (i18n, code, content) => {
  if (i18n.hasResourceBundle(code, 'translation')) {
    i18n.removeResourceBundle(code, 'translation')
  }
  i18n.addResourceBundle(code, 'translation', discardBlankValues(content), true, true)
}

/**
 * CU11 — after a write (save, undo or restore) answers with a non-null `change`, re-reads the
 * edited language's file and applies it to THIS session, but only when the editor is working in the
 * language their own session runs on [RN32].
 *
 * ⚠️ Deliberately does not degrade to the base language on failure, unlike `applyUserLanguage`: a
 * session already running stays exactly as it was rather than losing its customisations because one
 * re-read did not answer [S134].
 *
 * @param {Object} i18n The i18next instance
 * @param {string} code The language code that was just edited
 * @returns {Promise<void>}
 */
export const rereadCustomDictionaryInSession = async (i18n, code) => {
  if (i18n.language !== code) return
  const { available, content } = await fetchLanguageContent(code)
  if (!available) return
  applyCustomDictionary(i18n, code, content)
  await i18n.changeLanguage(code)
}

/**
 * Resolves, loads and applies the language of a user, and answers what actually happened.
 *
 * The order of the three steps is the contract: resolve which dictionary is wanted, register it, and
 * only then apply the language.
 *
 * @param {Object} i18n The i18next instance
 * @param {string} selectedCode The code saved against the user -- what they chose
 * @param {Array<Object>} catalogue The catalogue
 * @returns {Promise<{appliedCode: string, contentCode: string, contentLoaded: boolean}>} What was
 *   applied, which dictionary was wanted, and whether it could be loaded
 */
export const applyUserLanguage = async (i18n, selectedCode, catalogue) => {
  const contentCode = contentCodeFor(selectedCode, catalogue)

  // An installation with no custom language makes not one extra request, and its start-up is the one
  // it had before this story.
  if (!needsFetchedContent(contentCode)) {
    await i18n.changeLanguage(contentCode)
    return { appliedCode: contentCode, contentCode, contentLoaded: false }
  }

  const { available, content } = await fetchLanguageContent(contentCode)

  // The dictionary is not there. The application keeps every one of its words by running on the base
  // language, the user is told nothing -- the diagnosis belongs in the server log, not on their
  // screen -- and the language stays in the selector, because a missing file is fixed by copying it
  // back, while a language that vanishes without explanation is not.
  // ⚠️ "Not there" is NOT "empty": an empty dictionary is a language that exists and redefines
  // nothing yet, and it is applied below like any other (amendment 4).
  if (!available) {
    const fallback = baseLanguageCodeOf(contentCode)
    await i18n.changeLanguage(fallback)
    return { appliedCode: fallback, contentCode, contentLoaded: false }
  }

  // Register BEFORE applying. The other order paints the standard words first and the customer's a
  // moment later, which is the flicker the rules forbid adding. An empty bundle is registered too:
  // it is what makes the custom code a known language for i18next, so that every key it does not
  // redefine falls to its base through the `es-acme → es → en` hierarchy.
  applyCustomDictionary(i18n, contentCode, content)
  await i18n.changeLanguage(contentCode)
  return { appliedCode: contentCode, contentCode, contentLoaded: true }
}
